/**
 * Token Storage Abstraction Layer
 * 
 * Provides a unified interface for storing and retrieving authentication tokens.
 * This abstraction layer delegates to the existing auth store, allowing you to
 * change the storage mechanism later without modifying consuming code.
 * 
 * Currently delegates to useAuthStore for compatibility with the existing system.
 * When ready to migrate, update the implementation here.
 * 
 * @example
 * ```ts
 * const tokenStorage = useTokenStorage();
 * 
 * // Store credentials with user type (handles role_id mapping)
 * tokenStorage.setCredentials(token, user, 'client');
 * 
 * // Get the stored token
 * const token = tokenStorage.getToken();
 * 
 * // Clear all credentials
 * tokenStorage.clearCredentials();
 * ```
 */

import { useAuthStore } from '@/store/auth';

export type UserType = 'client' | 'expert';

export interface TokenStorageUser {
  id?: number;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  role_id?: number;
  [key: string]: unknown;
}

export interface TokenStorageData {
  token: string | null;
  user: TokenStorageUser | null;
}

/**
 * Maps user type to role_id for router guard compatibility
 * - admin = 1
 * - client = 2
 * - expert = 3
 */
function mapUserTypeToRoleId(userType: UserType): number {
  switch (userType) {
    case 'client':
      return 2;
    case 'expert':
      return 3;
    default:
      return 2; // Default to client
  }
}

export function useTokenStorage() {
  const authStore = useAuthStore();

  /**
   * Store credentials (token and user) using the auth store.
   * Automatically maps userType to role_id for router guard compatibility.
   * This sets the API header, Pinia state, and persists to storage.
   * 
   * @param token - The authentication token
   * @param user - The user object from the API response
   * @param userType - The user type ('client' or 'expert') for role_id mapping
   */
  function setCredentials(token: string, user: TokenStorageUser, userType: UserType): void {
    // Add role_id based on user type for router guard compatibility
    const userWithRole: TokenStorageUser = {
      ...user,
      role_id: user.role_id ?? mapUserTypeToRoleId(userType),
    };
    
    authStore.setCurrentUser(userWithRole, token);
  }

  /**
   * Retrieve the stored authentication token
   */
  function getToken(): string | null {
    return authStore.token || null;
  }

  /**
   * Check if a token exists
   */
  function hasToken(): boolean {
    return !!authStore.token;
  }

  /**
   * Retrieve the stored user data
   */
  function getUser(): TokenStorageUser | null {
    return authStore.user || null;
  }

  /**
   * Get both token and user data
   */
  function getCredentials(): TokenStorageData {
    return {
      token: getToken(),
      user: getUser(),
    };
  }

  /**
   * Clear all stored authentication data
   */
  function clearCredentials(): void {
    authStore.logout();
  }

  return {
    // Combined operations (recommended)
    setCredentials,
    getCredentials,
    clearCredentials,
    
    // Individual accessors
    getToken,
    hasToken,
    getUser,
  };
}
